import { useApolloClient } from '@apollo/client/react';
import { useCallback } from 'react';

import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useRevokeTwaDeviceToken } from '#src/hooks/useTwaDeviceToken.ts';
import { AuthBroadCastChannel } from '#src/lib/broadcastChannel.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { client as httpClient, type HttpResponse } from '#src/lib/http.ts';

export const useLogout = () => {
  const client = useApolloClient();
  const track = useEvents();
  const { isMobileApp, postMessage: mobileAppPostMessage } = useIsMobileApp();
  const revokeTwaDeviceToken = useRevokeTwaDeviceToken();

  return useCallback(async () => {
    // needs the session: must settle before the sign out request. Failures are
    // reported by the mutation and never block the logout.
    await revokeTwaDeviceToken().catch(() => {});

    return httpClient
      .delete('/users/sign_out.json')
      .then(async response => {
        if (response.status === 204) {
          track('[Client] Log Out');
          if (window.analytics) {
            window.analytics.reset();
          }
        }
        await client.clearStore();
        // Root, not a reload: reloading a protected path seeds a redirectUrl via
        // the UNAUTHORIZED bounce, which then suppresses the next signup's onboarding.
        // oxlint-disable-next-line no-restricted-properties
        window.location.replace('/');

        // log out all tabs
        AuthBroadCastChannel.postMessage('logged_out');

        // log out Flutter webview-based app
        if (isMobileApp) {
          mobileAppPostMessage('logout', { isExplicit: true });
        }

        return response;
      })
      .catch((error: Error) => ({ error: error.message }));
  }, [client, isMobileApp, mobileAppPostMessage, revokeTwaDeviceToken, track]);
};

export type { HttpResponse };
